import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { Button } from '../ui/button';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';

const TOUR_STEPS = [
  {
    id: 'dashboard',
    target: '[data-tour="dashboard-stats"]',
    title: 'Suas Estatísticas',
    description: 'Aqui você vê um resumo de todas as suas viagens, gastos e próximos eventos.',
    position: 'bottom',
  },
  {
    id: 'create-trip',
    target: '[data-tour="create-trip-btn"]',
    title: 'Criar Nova Viagem',
    description: 'Clique aqui para criar uma viagem e convidar seus amigos!',
    position: 'bottom',
  },
  {
    id: 'join-trip',
    target: '[data-tour="join-trip-btn"]',
    title: 'Entrar com Código',
    description: 'Recebeu um código de convite? Use aqui para entrar em uma viagem!',
    position: 'bottom',
  },
  {
    id: 'notifications',
    target: '[data-testid="notification-bell"]',
    title: 'Notificações',
    description: 'Fique por dentro de novos pagamentos, votações e atualizações.',
    position: 'bottom-left',
  },
  {
    id: 'theme',
    target: '[data-testid="theme-toggle"]',
    title: 'Modo Escuro',
    description: 'Prefere o dark mode? Alterne aqui! 🌙',
    position: 'bottom-left',
  },
];

const Tooltip = ({ step, onNext, onPrev, onClose, isFirst, isLast, currentStep, totalSteps }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [arrowPosition, setArrowPosition] = useState('top');

  useEffect(() => {
    const target = document.querySelector(step.target);
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const tooltipWidth = 300;
    const tooltipHeight = 180;
    const padding = 12;

    let top, left;
    let arrow = 'top';

    switch (step.position) {
      case 'bottom':
        top = rect.bottom + padding;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        arrow = 'top';
        break;
      case 'bottom-left':
        top = rect.bottom + padding;
        left = rect.right - tooltipWidth;
        arrow = 'top-right';
        break;
      case 'top':
        top = rect.top - tooltipHeight - padding;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        arrow = 'bottom';
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - padding;
        arrow = 'right';
        break;
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + padding;
        arrow = 'left';
        break;
      default:
        top = rect.bottom + padding;
        left = rect.left;
    }

    // Keep within viewport
    left = Math.max(16, Math.min(left, window.innerWidth - tooltipWidth - 16));
    top = Math.max(16, Math.min(top, window.innerHeight - tooltipHeight - 16));

    setPosition({ top, left });
    setArrowPosition(arrow);

    // Highlight target
    target.classList.add('tour-highlight');
    return () => target.classList.remove('tour-highlight');
  }, [step]);

  return createPortal(
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-[9998]"
        onClick={onClose}
      />

      {/* Tooltip */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 10 }}
        style={{ top: position.top, left: position.left }}
        className="fixed z-[9999] w-[300px] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Arrow */}
        <div
          className={`absolute w-3 h-3 bg-white dark:bg-slate-800 transform rotate-45 ${
            arrowPosition === 'top' ? '-top-1.5 left-1/2 -translate-x-1/2' :
            arrowPosition === 'top-right' ? '-top-1.5 right-6' :
            arrowPosition === 'bottom' ? '-bottom-1.5 left-1/2 -translate-x-1/2' :
            arrowPosition === 'left' ? '-left-1.5 top-1/2 -translate-y-1/2' :
            '-right-1.5 top-1/2 -translate-y-1/2'
          }`}
        />

        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-pink-500 p-4 text-white">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium opacity-80">
              {currentStep} de {totalSteps}
            </span>
            <button
              onClick={onClose}
              className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <h3 className="font-bold text-lg mt-2">{step.title}</h3>
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-slate-600 dark:text-slate-300 text-sm">
            {step.description}
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 pt-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={onPrev}
            disabled={isFirst}
            className="text-slate-500"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Anterior
          </Button>
          <Button
            size="sm"
            onClick={onNext}
            className="bg-orange-500 hover:bg-orange-600 rounded-full"
          >
            {isLast ? 'Finalizar' : 'Próximo'}
            {!isLast && <ChevronRight className="w-4 h-4 ml-1" />}
          </Button>
        </div>
      </motion.div>
    </>,
    document.body
  );
};

export const GuidedTour = ({ steps = TOUR_STEPS, onComplete }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const handleNext = () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    onComplete?.();
  };

  if (!isVisible) return null;

  const currentStep = steps[currentStepIndex];
  if (!currentStep) return null;

  return (
    <AnimatePresence>
      <Tooltip
        step={currentStep}
        onNext={handleNext}
        onPrev={handlePrev}
        onClose={handleClose}
        isFirst={currentStepIndex === 0}
        isLast={currentStepIndex === steps.length - 1}
        currentStep={currentStepIndex + 1}
        totalSteps={steps.length}
      />
    </AnimatePresence>
  );
};

// CSS for highlighting target elements
const tourStyles = `
  .tour-highlight {
    position: relative;
    z-index: 9999 !important;
    box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.5), 0 0 20px rgba(249, 115, 22, 0.3) !important;
    border-radius: 8px;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleEl = document.createElement('style');
  styleEl.textContent = tourStyles;
  document.head.appendChild(styleEl);
}

export default GuidedTour;
